
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Building, KeyRound } from 'lucide-react';
import { PasswordChangeForm } from '@/components/auth/PasswordChangeForm';
import CompanyOverview from '@/components/company/dashboard/CompanyOverview';
import PerksForm from '@/components/company/PerksForm';
import ImageUploadForm from '@/components/company/ImageUploadForm';
import CompanyJobsTab from '@/components/company/dashboard/CompanyJobsTab';
import CompanyLinksTab from '@/components/company/dashboard/CompanyLinksTab';
import CompanySettingsTab from '@/components/company/dashboard/CompanySettingsTab';

const CompanyDashboard = () => {
  const { user, isCompany, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("overview");
  
  const companyName = user?.user_metadata?.company_name || 'Your Company';

  const handleLogout = async () => {
    try {
      await logout();
      toast({
        title: "Logged out",
        description: "You have been successfully logged out",
      });
      navigate('/');
    } catch (error) {
      console.error("Logout error:", error);
      toast({
        title: "Logout failed",
        description: "There was an issue logging out. Please try again.",
        variant: "destructive"
      });
    }
  };

  if (!user || !isCompany) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Loading company dashboard...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 pt-24 px-6 pb-16">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <Building className="h-7 w-7 text-redflags-red" />
            <h1 className="text-3xl font-bold text-redflags-dark">{companyName} Dashboard</h1>
          </div>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="perks">Perks</TabsTrigger>
              <TabsTrigger value="images">Images</TabsTrigger>
              <TabsTrigger value="jobs">Jobs</TabsTrigger>
              <TabsTrigger value="links">Links</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
            </TabsList>
            
            <TabsContent value="overview" className="space-y-6">
              <CompanyOverview />
            </TabsContent>
            
            <TabsContent value="perks" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Company Perks</CardTitle>
                </CardHeader>
                <CardContent>
                  <PerksForm />
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="images" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Company Images</CardTitle>
                </CardHeader>
                <CardContent>
                  <ImageUploadForm />
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="jobs" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Job Listings</CardTitle>
                </CardHeader>
                <CardContent>
                  <CompanyJobsTab />
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="links" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>External Links</CardTitle>
                </CardHeader>
                <CardContent>
                  <CompanyLinksTab />
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="settings" className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <KeyRound className="w-5 h-5 text-redflags-red" />
                    <CardTitle>Change Password</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <PasswordChangeForm />
                </CardContent>
              </Card>
              
              <div className="flex justify-end">
                <Button variant="destructive" onClick={handleLogout}>Log Out</Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default CompanyDashboard;
